<template>
    <div :class="['field', 'radio-cards-field', { 'error': hasError }]">
        <label class="label" v-if="label">{{ labelText }}</label>

        <div class="radio-cards">
            <label
                v-for="option in computedOptions"
                :key="option.value"
                class="radio-card"
                :class="{ 'is-selected': data[name] === option.value }"
            >
                <input
                    type="radio"
                    :id="`${id}-${option.value}`"
                    :name="name"
                    :value="option.value"
                    v-model="data[name]"
                    :disabled="disabled || option.disabled"
                    @change="emit"
                />
                <div class="card-content">
                    <div class="card-title">{{ option.name }}</div>
                    <div class="card-description" v-if="option.description">{{ option.description }}</div>
                </div>
            </label>
        </div>

        <div class="hover-area"
             v-if="hasError"
             @mouseover="showErrorMessage"
             @mouseleave="hideErrorMessage">
            <div class="message-box">
                <div class="error-message" v-show="errorMessageVisible">
                    {{ errorMessage }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InputHelper from '../../utils/InputHelper'

export default InputHelper.extend({
    name: 'RadioCards',
    props: {
        options: {
            type: Array,
            required: true
        },
        valuekey: {
            type: String,
            default: 'value'
        },
        namekey: {
            type: String,
            default: 'name'
        },
        descriptionkey: {
            type: String,
            default: 'description'
        }
    },
    computed: {
        computedOptions() {
            return this.options.map(option => ({
                value: option[this.valuekey],
                name: option[this.namekey],
                description: option[this.descriptionkey] || null,
                disabled: option.disabled || false
            }))
        }
    },
    created() {
        this.$watch(`data.${this.name}`, value => {
            this.$emit('input', value)
        })
    }
})
</script>

<style lang="scss" scoped>
.radio-cards-field {
    .label {
        margin-bottom: 0.75rem;
    }
}

.radio-cards {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.radio-card {
    display: flex;
    cursor: pointer;
    padding: 1.25rem 1.5625rem;
    align-items: flex-start;
    gap: 1.25rem;
    border: 1px solid #E5E5E5;
    background: #FFF;
    border-radius: 0.625rem;
    transition: all 0.2s ease;

    &:hover:not(.is-disabled) {
        border-color: #3BC173;
    }

    &.is-selected {
        border-color: #3BC173;

        input[type="radio"] {
            &::before {
                background-color: #3BC173;
                transform: scale(1);
            }
        }
    }

    &.is-disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    input[type="radio"] {
        flex-shrink: 0;
        width: 1.6rem;
        height: 1.6rem;
        align-self: center;
        margin: 0;
        padding: 0;
        appearance: none;
        border-radius: 50%;
        position: relative;
        box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
        cursor: inherit;

        &::before {
            content: '';
            position: absolute;
            top: 0.25rem;
            left: 0.25rem;
            width: 1.1rem;
            height: 1.1rem;
            border-radius: 50%;
            background-color: transparent;
            transform: scale(0);
            transition: transform 0.2s ease;
        }

        &:checked {
            border-color: #2CC36B;
        }

        &:disabled {
            cursor: not-allowed;
        }
    }

    .card-content {
        display: flex;
        padding: 0 0.625rem 0 0;
        flex-direction: column;
        align-items: flex-start;
        flex: 1 0 0;
        align-self: stretch;
    }

    .card-title {
        color: #1F1F1F;
        font-size: 1rem;
        font-style: normal;
        line-height: normal;
    }

    .card-description {
        color: #6A7272;
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        margin-top: 0.25rem;
    }
}

.error {
    .radio-card {
        border-color: #ff3860;
    }
}
</style>
